import { InsurancePaymentActionEnum } from 'src/app/models/enum/ledger/insurance-payment-action.enum';
import { ConversionUtilities } from 'src/app/utilities/conversion.utilities';
import { DataARClaimItemListModel } from '../models/data-ar-claim-item-list';
import { DataARClaimListModel } from '../models/data-ar-claim-list';
import { InsurancePaymentOverpayListModel } from '../models/insurance-payment-overpay-list.model';

// same value shown in the Patient Estimate column
export function getRemainingPatientEstimate(claimitem: DataARClaimItemListModel): number {
  return ConversionUtilities.roundDecimal(claimitem.PatientEstimateAmount ?? claimitem.PatientResponsibility ?? 0);
}

export function isApplyPatientPaymentAction(actionId: string): boolean {
  return actionId?.toUpperCase() === InsurancePaymentActionEnum.ApplyPatientPayment.toUpperCase();
}

// clear Apply Patient Payment selections that are no longer offered (no patient estimate left)
export function clearUnavailablePatientPaymentActions(claims: DataARClaimListModel[]): void {
  (claims ?? []).forEach(claim => {
    (claim.dataARClaimListItems ?? []).forEach(claimitem => {
      if (isApplyPatientPaymentAction(claimitem.enumARInsurancePaymentActionID)
        && getRemainingPatientEstimate(claimitem) <= 0) {
        claimitem.enumARInsurancePaymentActionID = null;
      }
    });
  });
}

// builds the overpay entries sent to save-insurance-payment-distributions for an overpaid claim item
export function buildClaimItemOverpayList(claim: DataARClaimListModel, claimItem: DataARClaimItemListModel, paymentId?: string): InsurancePaymentOverpayListModel[] {
  const buildItemOverpay = (amount: number, actionId: string): InsurancePaymentOverpayListModel => {
    const itemOverpay = new InsurancePaymentOverpayListModel();
    itemOverpay.PatientID = claim.PatientID;
    if (paymentId) {
      itemOverpay.PaymentID = paymentId;
    }
    itemOverpay.ClaimItemID = claimItem.ARClaimItemID;
    itemOverpay.ClaimNumber = claim.TDOClaimNumber;
    itemOverpay.Amount = amount;
    itemOverpay.LOCID = claim.LOCID;
    itemOverpay.enumARInsurancePaymentActionID = actionId;
    itemOverpay.LedgerID = claimItem.LedgerID;
    itemOverpay.ARTransactionItemID = claim.ARTransactionItemID;
    return itemOverpay;
  };
  const overpayAmount = claimItem.ApplyPayment - (Math.max(claimItem.AmountOutstanding, 0));
  const patientEstimate = getRemainingPatientEstimate(claimItem);

  // applying more than the patient estimate would leave a negative patient balance:
  // apply at most the estimate to the patient and credit the rest to insurance
  if (isApplyPatientPaymentAction(claimItem.enumARInsurancePaymentActionID)) {
    if (patientEstimate <= 0) {
      return [buildItemOverpay(overpayAmount, InsurancePaymentActionEnum.InsuranceCredit)];
    }
    if (patientEstimate < ConversionUtilities.roundDecimal(overpayAmount)) {
      return [
        buildItemOverpay(patientEstimate, claimItem.enumARInsurancePaymentActionID),
        buildItemOverpay(ConversionUtilities.roundDecimal(overpayAmount - patientEstimate), InsurancePaymentActionEnum.InsuranceCredit)
      ];
    }
  }
  return [buildItemOverpay(overpayAmount, claimItem.enumARInsurancePaymentActionID)];
}

// an overpaid claim item still waiting for the user to pick an action
export function hasOverpaidItemWithoutAction(claims: DataARClaimListModel[]): boolean {
  return (claims ?? []).some(claim => (claim.dataARClaimListItems ?? []).some(item =>
    item.ApplyPayment > 0 && item.AmountOutstanding < item.ApplyPayment && !item.enumARInsurancePaymentActionID));
}
